import _ from 'lodash';
import { RecordTypeEnum, RoleEnum } from 'src/api/models/Enums';
import { NOLEGGIATORI, NOLEGGIATORI_QUOTATORE } from 'src/utils/constants';
import { useAuth } from './useAuth';

type QuickoHelper = {
	isSegnalatore: boolean;
	canCreateReport: boolean;
	extractSignalingData: (data: any) => any;
	updateCommissions: (bestOffer?: any, inputCommissionValue?: string) => any;
	computeSegnalatoreCommissions: (commissionValue: string) => any;
	isZero: (value: string) => boolean;
	updateQuotationCommissions: (
		responseQuotations: any,
		commissionValue?: string
	) => any;
};

export function useQuickoHelper(): QuickoHelper {
	const { userRole, userProvvigioniSegnalatore } = useAuth();
	const isSegnalatore = userRole === RoleEnum.SEGNALATORE;
	const canCreateReport =
		isSegnalatore && process.env.REACT_APP_QUICKO_ENABLED === '1';

	const updateCommissions = (bestOffer: any, inputCommissionValue?: string) => {
		if (!bestOffer) return bestOffer;
		bestOffer.updated_commissioni = bestOffer.commissioni;

		if (!isSegnalatore) return bestOffer;

		if (inputCommissionValue && !isZero(inputCommissionValue)) {
			const commissionValue = parseFloat(inputCommissionValue);
			bestOffer.updated_commissioni =
				bestOffer.noleggiatore === 'leasys'
					? parseFloat(
							(
								bestOffer.commissioni *
								(userProvvigioniSegnalatore
									? userProvvigioniSegnalatore / 100
									: 1)
							).toFixed(2)
					  )
					: commissionValue;
			return bestOffer;
		}

		const updatedCommissions =
			bestOffer.commissioni *
			(userProvvigioniSegnalatore ? userProvvigioniSegnalatore / 100 : 1);

		bestOffer.updated_commissioni = parseFloat(updatedCommissions.toFixed(2));

		return bestOffer;
	};

	const updateQuotationCommissions = (
		responseQuotations: any,
		commissionValue?: string
	) => {
		if (!responseQuotations) return responseQuotations;

		responseQuotations.best_offers = (responseQuotations.best_offers || []).map(
			(offer) => updateCommissions(offer, commissionValue)
		);
		responseQuotations.ai_offers = (responseQuotations.ai_offers || []).map(
			(offer) => updateCommissions(offer, commissionValue)
		);
		responseQuotations.similar_offers = (
			responseQuotations.similar_offers || []
		).map((offer) => updateCommissions(offer, commissionValue));

		return responseQuotations;
	};

	const isZero = (numberValue: string) => {
		const value = _.isString(numberValue) ? numberValue.trim() : numberValue;
		return /^(?:-)?0(?:\.0{1,2})?$/.test(value);
	};

	const computeSegnalatoreCommissions = (commissionValue: string) => {
		if (!isSegnalatore || !commissionValue) return commissionValue;
		if (!userProvvigioniSegnalatore) return commissionValue;

		const partialAmount = parseFloat(commissionValue);
		const percentageDecimal = userProvvigioniSegnalatore / 100;
		const value = partialAmount / percentageDecimal;
		return parseFloat(value.toFixed(2));
	};

	return {
		extractSignalingData,
		isSegnalatore,
		canCreateReport,
		updateCommissions,
		computeSegnalatoreCommissions,
		isZero,
		updateQuotationCommissions,
	};
}

function extractSignalingData(data: any) {
	let vehicle = _.pick(data, [
		'id',
		'dati_veicolo',
		'dettagli_veicolo',
		'optionals',
		'stato_corrente',
		'storico_azioni',
		'lista_offerte',
		'image_list',
		'preferiti',
		'richieste_preventivi',
		'lista_noleggiatori_quotatore',
		'visibilita_segnalatore',
	]);
	vehicle = _.omit(vehicle, ['dettagli_veicolo.promo_note']);

	vehicle.image_list = (vehicle.image_list || []).map((img) => ({
		...img,
		url: img.base64?.replace(
			'data:application/octet-stream;base64,',
			'data:image/png;base64,'
		),
	}));

	const noleggiatore = data.row.noleggiatore.toUpperCase();
	const clientType = _.get(data, 'pdf.clientType');
	const name = _.get(data, 'pdf.name');
	const accountInfo: any = {};

	if (clientType === 'private') {
		accountInfo.RecordTypeId = RecordTypeEnum.Privato;
		accountInfo.RecordType = 'PRIVATO';
		const [FirstName, LastName] = name?.trim()?.split(' ');
		accountInfo.FirstName = FirstName;
		accountInfo.LastName = LastName;
	}

	if (clientType === 'company') {
		accountInfo.RecordTypeId = RecordTypeEnum.Azienda;
		accountInfo.RecordType = 'AZIENDA';
		accountInfo.Company = name;
	}

	if (clientType === 'libero_professionista') {
		accountInfo.RecordTypeId = RecordTypeEnum.Libero_Professionista;
		accountInfo.RecordType = 'LIBERO PROFESSIONISTA';
		const [FirstName, LastName] = name?.trim()?.split(' ');
		accountInfo.FirstName = FirstName;
		accountInfo.LastName = LastName;
	}

	let noleggiatoreHmp = Object.values(NOLEGGIATORI_QUOTATORE)
		.find((item) =>
			(item.noleggiatori_quicko as string[]).includes(noleggiatore)
		)
		?.name?.toUpperCase();

	if (noleggiatoreHmp === NOLEGGIATORI.ALD) {
		noleggiatoreHmp = 'AYVENS';
	}

	return {
		vehicle,
		bestOffer: data.row,
		clientType: _.get(data, 'pdf.clientType'),
		accountInfo,
		noleggiatore: noleggiatoreHmp,
	};
}
